/**
 * An EventSource that reconnects (gaps plan §9).
 *
 * `render-status.tsx` deliberately gives up on the first error, and that is
 * right for a 45-second render: the job is nearly done, the files are wanted
 * either way, and a reconnect loop would outlive the thing it was watching.
 *
 * It is wrong here. A job can run for hours, a laptop sleeps, a proxy times
 * an idle stream out, and `tsx watch` restarts the server on every save --
 * and the whole promise of this feature is that none of those lose the work.
 * So this reconnects with backoff, and because the stream replays current
 * state on connect, a reconnect is indistinguishable from never having
 * dropped.
 */
import { useEffect, useRef, useState } from 'react'

export type SseStatus = 'connecting' | 'open' | 'retrying' | 'closed'

const FIRST_DELAY = 500
const MAX_DELAY = 15_000

export function useEventSource(
  /** Null closes the stream and keeps it closed. */
  url: string | null,
  handlers: Record<string, (data: unknown) => void>,
  /** Called when the server says the work is over, so we stop reconnecting. */
  options: { closeWhen?: (event: string, data: unknown) => boolean } = {},
): SseStatus {
  const [status, setStatus] = useState<SseStatus>('closed')

  // Handlers change identity on every render; the stream must not.
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers
  const closeWhenRef = useRef(options.closeWhen)
  closeWhenRef.current = options.closeWhen

  useEffect(() => {
    if (!url) { setStatus('closed'); return }

    let es: EventSource | null = null
    let retry: ReturnType<typeof setTimeout> | null = null
    let delay = FIRST_DELAY
    let stopped = false

    const connect = () => {
      if (stopped) return
      setStatus((s) => (s === 'closed' ? 'connecting' : s))
      es = new EventSource(url)

      es.onopen = () => {
        delay = FIRST_DELAY // a successful connection resets the backoff
        setStatus('open')
      }

      for (const name of Object.keys(handlersRef.current)) {
        es.addEventListener(name, (e) => {
          let data: unknown
          try {
            data = JSON.parse((e as MessageEvent).data)
          } catch {
            return // a heartbeat comment, or a truncated frame
          }
          handlersRef.current[name]?.(data)
          if (closeWhenRef.current?.(name, data)) {
            stopped = true
            es?.close()
            setStatus('closed')
          }
        })
      }

      es.onerror = () => {
        es?.close()
        es = null
        if (stopped) return
        // EventSource reconnects on its own, but with no backoff and no way
        // to stop; closing and rescheduling is what makes the interval ours.
        setStatus('retrying')
        retry = setTimeout(connect, delay)
        delay = Math.min(delay * 2, MAX_DELAY)
      }
    }

    connect()
    return () => {
      stopped = true
      if (retry) clearTimeout(retry)
      es?.close()
      setStatus('closed')
    }
  }, [url])

  return status
}
